import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function PasswordResetPage() {
  const { changePassword, logout } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    const result = await changePassword(newPassword);
    setLoading(false);
    if (result.success) {
      toast.success('Password changed successfully');
    } else {
      toast.error(result.error || 'Failed to change password');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-700">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto p-3 bg-amber-500/20 rounded-full w-fit mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">Password Reset Required</CardTitle>
          <p className="text-zinc-400 text-sm mt-2">
            You must change your temporary password before continuing.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-zinc-800 border-zinc-600 text-white pl-10"
                  placeholder="Enter new password (min 6 characters)"
                  required
                  data-testid="new-password-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-zinc-500" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-zinc-800 border-zinc-600 text-white pl-10"
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="change-password-button"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full border-zinc-600 text-zinc-400 hover:bg-zinc-800"
              onClick={logout}
              data-testid="logout-button"
            >
              Sign Out
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
